import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ValuationComparable, ValuationComparablesResponse, ValuationProperty } from '../../../@types/Valuation';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import Spinner from '../../common/Spinner';

interface Props {
  companyId: number;
  coproId: number;
  property: ValuationProperty;
  postalCode: string;
  selected: ValuationComparable[];
  onSelectionChange: (comparables: ValuationComparable[]) => void;
  onBack: () => void;
  onNext: () => void;
  accentColor: string;
}

const fmtCurrency = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

export default function ValuationComparables({ companyId, coproId, property, postalCode, selected, onSelectionChange, onBack, onNext, accentColor }: Props) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [comparables, setComparables] = useState<ValuationComparable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const isSelected = (c: ValuationComparable) => selected.some((s) => s.reference === c.reference);

  const toggle = (c: ValuationComparable) => {
    if (isSelected(c)) {
      onSelectionChange(selected.filter((s) => s.reference !== c.reference));
    } else {
      onSelectionChange([...selected, c]);
    }
  };

  const fetchComparables = useCallback(async() => {
    setLoading(true);
    setError(null);
    try {
      const rooms = property.rooms;
      const roomsMin = rooms >= 3 ? rooms - 1 : rooms;
      const roomsMax = rooms >= 3 ? rooms + 1 : rooms;

      const params = new URLSearchParams({
        propertyType: property.type,
        postalCode,
        roomsMin: String(roomsMin),
        roomsMax: String(roomsMax),
        priceMin: String(property.price),
        priceMax: String(property.priceMax),
        area: String(property.area ?? 0),
      });

      const url = `/api/companies/${companyId}/copros/${coproId}/valuation/comparables?${params.toString()}`;
      const resp = await fetch(url, JWTHeader(jwt));

      if (!resp.ok) { throw new Error(`Erreur ${resp.status}`); }

      const data: ValuationComparablesResponse = await resp.json();
      setComparables(data.data);
      setFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  }, [companyId, coproId, property, postalCode, jwt]);

  useEffect(() => {
    if (!fetched) { void fetchComparables(); }
  }, [fetchComparables, fetched]);

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">
            {loading ? 'Recherche en cours...' : `${comparables.length} bien${comparables.length !== 1 ? 's' : ''} comparable${comparables.length !== 1 ? 's' : ''}`}
          </p>
          {selected.length > 0 && (
            <p className="text-xs text-slate-500">{selected.length} sélectionné{selected.length > 1 ? 's' : ''} pour le rapport</p>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-10">
          <Spinner />
          <p className="text-sm text-slate-500">Cette opération peut prendre un moment...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && comparables.length === 0 && fetched && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-4">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="text-sm text-blue-700">
            Aucun bien comparable trouvé. Ajustez les critères (type de bien, nombre de pièces, fourchette de prix) et relancez la recherche.
          </p>
        </div>
      )}

      {/* Grid of comparables */}
      {!loading && comparables.length > 0 && (
        <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2">
          {comparables.map((c) => {
            const active = isSelected(c);
            return (
              <div
                key={c.reference}
                className={`group relative cursor-pointer overflow-hidden rounded-xl border-2 bg-white transition-all ${active ? 'shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
                style={active ? { borderColor: accentColor } : undefined}
                onClick={() => toggle(c)}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  {c.image ? (
                    <img
                      src={c.image}
                      alt={c.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-300">
                      <MapPinIcon className="h-10 w-10" />
                    </div>
                  )}
                  {/* Overlay badges */}
                  <div className="absolute right-0 bottom-0 left-0 flex flex-wrap gap-1 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
                    <span className="rounded bg-white/90 px-2 py-0.5 text-xs font-semibold text-slate-800">{fmtCurrency(c.price)}</span>
                    {c.area !== null && c.area > 0 && <span className="rounded bg-white/90 px-2 py-0.5 text-xs text-slate-700">{c.area} m²</span>}
                    {c.rooms > 0 && <span className="rounded bg-white/90 px-2 py-0.5 text-xs text-slate-700">{c.rooms} p.</span>}
                  </div>
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2">
                    <div className={`flex h-6 w-6 items-center justify-center rounded-md border-2 transition ${active ? 'border-white bg-white' : 'border-white/70 bg-white/40'}`}>
                      {active && <CheckCircleIcon className="h-5 w-5" style={{ color: accentColor }} />}
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div className="p-3">
                  <h4 className="text-sm leading-tight font-medium text-slate-800">{c.title}</h4>
                  {c.pricePerSqm !== null && c.pricePerSqm > 0 && (
                    <p className="mt-0.5 text-xs text-slate-500">{fmtCurrency(c.pricePerSqm)}/m²</p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                    {c.locationCity && <span><MapPinIcon className="mr-0.5 inline h-3 w-3" />{c.locationCity} {c.locationPostalCode}</span>}
                    {c.source && (
                      c.url ? (
                        <a href={c.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600" onClick={(e) => e.stopPropagation()}>
                          {c.source}
                        </a>
                      ) : <span>{c.source}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          onClick={onBack}
        >
          ← Retour
        </button>
        <button
          type="button"
          className="rounded-lg px-6 py-2.5 text-sm font-medium text-white shadow-sm transition hover:brightness-110 disabled:opacity-50"
          style={{ backgroundColor: accentColor }}
          onClick={onNext}
        >
          Générer l&apos;avis de valeur
        </button>
      </div>
    </div>
  );
}
